import { isEmpty } from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';
import { routes } from '../../config';
import i18n from '../../i18n';
import { AuthReducerState, Channel } from '../../interfaces';
import { getImageByPath } from '../../queries/ats/common';
import { Layout } from '../../queries/ats/layout/types';
import { Swimlane } from '../Swimlane/Swimlane';
import ChannelsBannerListItem from './ChannelsBannerListItem';
import SvgSeeAll from './see-all-channels.svg';
import './style.scss';

interface Props {
  onlyInSubscription?: boolean;
  channels?: Channel[];
  isProgramArchive?: boolean;
  authReducer: AuthReducerState;
  isUnmanaged?: boolean;
  overrideTitle?: string;
  isGrid?: boolean;
  after?: React.ReactNode;
  sectionItems?: Layout.SectionItem[] | undefined;
  showLoginModal(): void;
}

const channelsLimit = 15;

class ChannelsBanner extends React.Component<Props> {
  get headerText() {
    const { isProgramArchive, overrideTitle } = this.props;

    if (overrideTitle !== undefined) {
      return overrideTitle;
    }

    if (isProgramArchive) {
      return i18n.t<string>('catchup tv channels');
    }

    return i18n.t<string>('watch tv');
  }

  get seeAllURI() {
    const { isProgramArchive } = this.props;

    if (this.seeAllBySectionItem?.slug) {
      return this.seeAllBySectionItem.slug;
    }

    const { base, programarchive, live } = routes.watchTv;
    const unmanagedPageLink = this.props.isUnmanaged ? routes.unmanaged.base : '';
    return `${unmanagedPageLink}${base}${isProgramArchive ? programarchive : live}`;
  }

  get seeAllBySectionItem() {
    const { sectionItems } = this.props;

    if (!sectionItems) {
      return undefined;
    }

    const seeAll = sectionItems.find((item) => item.type === 'page' && item.params?.behavior === 'see-all');

    if (sectionItems.length > 0 && seeAll !== undefined) {
      return seeAll;
    }

    return undefined;
  }

  get seeAllChannelsCard() {
    const seeAllTitle = this.seeAllBySectionItem?.name ?? i18n.t<string>('see all');
    const editorialImage = this.seeAllBySectionItem?.image
      ? getImageByPath(this.seeAllBySectionItem.image, 'wide', 200)
      : undefined;

    return (
      <div key="see-all" className={`see-all-wrapper ${!isEmpty(editorialImage) ? 'editorial-see-all-wrapper' : ''}`}>
        <Link to={this.seeAllURI}>
          <img
            crossOrigin=""
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            className={editorialImage ? 'editorial-see-all-img' : 'see-all-svg'}
            src={editorialImage ?? SvgSeeAll}
            alt={seeAllTitle}
            title={seeAllTitle}
          />
          <div className={'see-all-text'}>{seeAllTitle}</div>
        </Link>
      </div>
    );
  }

  renderChannelImage(channel: Channel) {
    return (
      <ChannelsBannerListItem key={channel.name} channel={channel} isProgramArchive={this.props.isProgramArchive} />
    );
  }

  renderGridView() {
    const { channels } = this.props;
    let channelImages: JSX.Element[];

    if (!channels || isEmpty(channels)) {
      channelImages = Array.from({ length: 15 }).map((_, index) => {
        return <ChannelsBannerListItem.Skeleton index={index} key={index} />;
      });
    } else {
      channelImages = channels.map((channel) => {
        return this.renderChannelImage(channel);
      });
    }

    return (
      <div className={'channelsGrid'}>
        {this.headerText.length > 0 && (
          <h3 title={this.headerText} className="watch-tv-header">
            {this.headerText}
          </h3>
        )}
        <ol>{channelImages}</ol>
      </div>
    );
  }

  renderSwimLaneView() {
    const { channels } = this.props;
    let channelImages: JSX.Element[];

    if (!channels || isEmpty(channels)) {
      channelImages = Array.from({ length: 15 }).map((_, index) => {
        return <ChannelsBannerListItem.Skeleton index={index} key={index} />;
      });
    } else {
      const channelsToUse = [...channels].splice(0, channelsLimit);
      channelImages = channelsToUse.map((channel) => {
        return this.renderChannelImage(channel);
      });
    }
    channelImages.push(this.seeAllChannelsCard);
    return (
      <Swimlane>
        <Swimlane.Header>
          <Swimlane.Title to={this.seeAllURI}>{this.headerText}</Swimlane.Title>
          <Swimlane.Buttons />
        </Swimlane.Header>
        <Swimlane.Slider className="channels-slider">{channelImages}</Swimlane.Slider>
      </Swimlane>
    );
  }

  render() {
    return this.props.isGrid ? this.renderGridView() : this.renderSwimLaneView();
  }
}

export default ChannelsBanner;
